import React, { useMemo } from "react";
import { useApolloClient } from "@apollo/client";
import {
  computeLiveScore,
  formatLiveScore,
  type LiveScore,
  type LiveScoreContent,
  type LiveScoreGlobalSet,
} from "@eaco/forms-core";
import { DOCUMENT_RESPONSE_SET_QUERY } from "../elements/queries";
import { cn } from "../lib/utils";

/**
 * The filler's live score, with global response sets read from the Apollo
 * cache (a rendered Dropdown has already fetched its set). Unresolved sets
 * leave the score approximate, shown with a `~`.
 */
export function useLiveScore(
  content: LiveScoreContent | null | undefined,
  values: Record<string, unknown>,
): LiveScore | null {
  const client = useApolloClient();
  return useMemo(() => {
    if (!content) return null;
    const resolve = (id: string): LiveScoreGlobalSet | null => {
      try {
        const cached = client.readQuery<{ documentResponseSet: LiveScoreGlobalSet | null }>({
          query: DOCUMENT_RESPONSE_SET_QUERY,
          variables: { id },
        });
        return cached?.documentResponseSet ?? null;
      } catch {
        return null;
      }
    };
    return computeLiveScore(content, values, resolve);
  }, [client, content, values]);
}

/**
 * "Score 12/20 · 60%" for a form whose designer chose to show the filler
 * their score. Renders nothing until the form has a scoring question.
 */
const LiveScoreChip: React.FC<{
  content: LiveScoreContent | null | undefined;
  values: Record<string, unknown>;
  className?: string;
}> = ({ content, values, className }) => {
  const score = useLiveScore(content, values);
  if (!score || !score.sawScoring) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-semibold tabular-nums text-foreground",
        className,
      )}
      aria-live="polite"
      title={score.approximate ? "Some questions' maximums are still loading" : undefined}
    >
      <span className="font-normal text-muted-foreground">Score</span>
      {formatLiveScore(score)}
    </span>
  );
};

export default LiveScoreChip;
